import { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import CloseRounded from "@mui/icons-material/CloseRounded";

import { APP_VERSION, CHANGELOG } from "@/lib/app-version";

/**
 * Small always-visible build marker (Sergey 2026-08-03). Tapping it
 * opens the release history, so it doubles as a "what changed?"
 * surface and as a quick way to confirm a deploy actually landed past
 * the service-worker cache.
 */
export function VersionBadge({ sx }: { sx?: object }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") setOpen(true);
        }}
        aria-label={`Version ${APP_VERSION} — view release history`}
        sx={(t: Theme) => ({
          display: "inline-flex",
          alignItems: "center",
          px: 0.75,
          py: 0.15,
          borderRadius: 1,
          cursor: "pointer",
          fontSize: "0.6875rem",
          fontWeight: 700,
          letterSpacing: 0.2,
          lineHeight: 1.6,
          color: t.palette.text.disabled,
          border: `1px solid ${t.palette.divider}`,
          userSelect: "none",
          "&:hover": {
            color: t.palette.text.secondary,
            borderColor: t.palette.text.disabled
          },
          ...sx
        })}
      >
        v{APP_VERSION}
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
          What's new
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontWeight: 400 }}
          >
            WHB Client · v{APP_VERSION}
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={() => setOpen(false)}
            sx={{ position: "absolute", top: 12, right: 12 }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {CHANGELOG.map((rel) => (
              <Box key={rel.version}>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  spacing={1}
                  sx={{ mb: 0.75 }}
                >
                  <Typography sx={{ fontWeight: 700 }}>
                    v{rel.version}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(`${rel.date}T00:00:00`).toLocaleDateString(
                      "en-US",
                      { year: "numeric", month: "short", day: "numeric" }
                    )}
                  </Typography>
                </Stack>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {rel.changes.map((c, i) => (
                    <Box
                      component="li"
                      key={i}
                      sx={{ mb: 0.5, color: "text.secondary" }}
                    >
                      <Typography variant="body2" component="span">
                        {c}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Divider sx={{ mt: 2 }} />
              </Box>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
